"use client"

import { cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import BaseImage, { type ImageProps } from "next/image"
import { useEffect, useState } from "react"

interface Props {
  imageSrc?: string
  alt: string
  className?: string
  containerClassName?: string
}

export const PLACEHOLDER_IMAGE_SRC = "/images/placeholder.svg"

/**
 * How long to wait before the one retry. A cold image is often still being
 * fetched upstream when the first attempt fails, and ready shortly after:
 * the media proxy gives up on Filebase after 8s without caching the timeout,
 * and Filebase keeps retrieving the file, so a few seconds later it is there.
 */
export const IMAGE_RETRY_DELAY_MS = 4_000

const RETRY_PARAM = "retry"

/**
 * `src` with a parameter that makes the browser request it again instead of
 * reusing the failed response. The media proxy ignores unknown parameters
 * when caching, so the retry still reads its cache.
 */
export function withRetryParam(src: string): string {
  const [path, hash] = src.split("#", 2)
  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}${RETRY_PARAM}=1${hash === undefined ? "" : `#${hash}`}`
}

type Attempt = "first" | "waiting" | "retry" | "failed"

/**
 * Tracks one image source through a single retry: a failed first load waits
 * {@link IMAGE_RETRY_DELAY_MS}, loads again with a cache-busting parameter,
 * and falls back to the placeholder only if that fails too.
 */
export function useImageWithRetry(imageSrc: string | undefined): {
  src: string
  onError: () => void
} {
  // The attempt belongs to the source it was made for, so a new source starts over at once
  const [state, setState] = useState<{
    source: string | undefined
    attempt: Attempt
  }>({ source: imageSrc, attempt: "first" })
  const attempt = state.source === imageSrc ? state.attempt : "first"

  useEffect(() => {
    if (attempt !== "waiting") return
    const timer = setTimeout(
      () => setState({ source: imageSrc, attempt: "retry" }),
      IMAGE_RETRY_DELAY_MS,
    )
    return () => clearTimeout(timer)
  }, [attempt, imageSrc])

  if (!imageSrc || attempt === "failed") {
    return { src: PLACEHOLDER_IMAGE_SRC, onError: () => {} }
  }

  return {
    src: attempt === "retry" ? withRetryParam(imageSrc) : imageSrc,
    // next/image can report one failed load twice, so only the retry's failure is final
    onError: () =>
      setState((current) => {
        const currentAttempt =
          current.source === imageSrc ? current.attempt : "first"
        if (currentAttempt === "first") {
          return { source: imageSrc, attempt: "waiting" }
        }
        if (currentAttempt === "retry") {
          return { source: imageSrc, attempt: "failed" }
        }
        return current
      }),
  }
}

export const Image = reatomComponent<Props>(
  ({ imageSrc, alt, className, containerClassName }) => {
    const { src, onError } = useImageWithRetry(imageSrc)
    const [loading, setLoading] = useState(true)

    return (
      <div className={cn("relative aspect-square w-full", containerClassName)}>
        {loading && (
          <div className="absolute z-10 h-full w-full animate-pulse rounded-md bg-primary/10" />
        )}
        <BaseImage
          className={cn("z-20", className)}
          unoptimized
          src={src}
          key={src}
          alt={alt}
          fill
          onLoadStart={() => {
            setLoading(true)
          }}
          onLoad={() => {
            setLoading(false)
          }}
          onError={onError}
        />
      </div>
    )
  },
  "Image",
)

/**
 * A fixed-size image with the same retry and placeholder as {@link Image},
 * for spots that size the image themselves, like a search result's icon.
 */
export const SizedImage = reatomComponent<
  Omit<ImageProps, "src" | "onError"> & { imageSrc?: string | null }
>(({ imageSrc, alt, ...props }) => {
  const { src, onError } = useImageWithRetry(imageSrc ?? undefined)
  return (
    <BaseImage {...props} src={src} key={src} alt={alt} onError={onError} />
  )
}, "SizedImage")
